import {
  format,
  nper,
  PAYMENTS_PER_YEAR,
  pmt,
  pv,
  RepaymentFrequency,
} from '@/lib/calculators/pmtUtils';

const PAYMENT_LABELS: Record<RepaymentFrequency, string> = {
  monthly: 'Monthly',
  fortnightly: 'Fortnightly',
  weekly: 'Weekly',
};

const MILESTONE_LABELS = ['Foundation / Slab', 'Frame', 'Lock-up', 'Fix-out', 'Completion'];

// ─── Borrowing Power ──────────────────────────────────────────────────────────

export type BorrowingPowerInput = {
  grossMonthlyIncome: number;
  monthlyExpenses: number;
  existingDebtRepayments: number;
  annualRate: number;
  loanTermYears: number;
  dscr: number;
};

export type BorrowingPowerResult = {
  maxLoan: string;
  maxLoanRaw: number;
  maxMonthlyRepayment: string;
  maxMonthlyRepaymentRaw: number;
  netMonthlyIncome: string;
};

function calculateBorrowingPower({
  grossMonthlyIncome,
  monthlyExpenses,
  existingDebtRepayments,
  annualRate,
  loanTermYears,
  dscr,
}: BorrowingPowerInput): BorrowingPowerResult {
  const netMonthlyIncome = grossMonthlyIncome - monthlyExpenses;
  const availableForDebt = dscr > 0 ? netMonthlyIncome / dscr : 0;
  const maxMonthlyRepaymentRaw = Math.max(availableForDebt - existingDebtRepayments, 0);

  if (maxMonthlyRepaymentRaw <= 0 || loanTermYears <= 0 || annualRate <= 0) {
    return {
      maxLoan: '0.00',
      maxLoanRaw: 0,
      maxMonthlyRepayment: '0.00',
      maxMonthlyRepaymentRaw: 0,
      netMonthlyIncome: format(Math.max(netMonthlyIncome, 0)),
    };
  }

  const periodicRate = annualRate / 100 / 12;
  const nPeriods = loanTermYears * 12;
  const maxLoanRaw = Math.max(pv(periodicRate, nPeriods, -maxMonthlyRepaymentRaw), 0);

  return {
    maxLoan: format(maxLoanRaw),
    maxLoanRaw,
    maxMonthlyRepayment: format(maxMonthlyRepaymentRaw),
    maxMonthlyRepaymentRaw,
    netMonthlyIncome: format(Math.max(netMonthlyIncome, 0)),
  };
}

// ─── Business Overdraft ───────────────────────────────────────────────────────

export type BusinessOverdraftInput = {
  overdraftLimit: number;
  amountDrawn: number;
  annualRate: number;
  daysDrawn: number;
};

export type BusinessOverdraftResult = {
  interestForPeriod: string;
  monthlyInterest: string;
  annualInterest: string;
  availableHeadroom: string;
  availableHeadroomRaw: number;
  amountDrawnRaw: number;
};

function calculateBusinessOverdraft({
  overdraftLimit,
  amountDrawn,
  annualRate,
  daysDrawn,
}: BusinessOverdraftInput): BusinessOverdraftResult {
  const availableHeadroomRaw = Math.max(overdraftLimit - amountDrawn, 0);

  if (amountDrawn <= 0) {
    return {
      interestForPeriod: '0.00',
      monthlyInterest: '0.00',
      annualInterest: '0.00',
      availableHeadroom: format(availableHeadroomRaw),
      availableHeadroomRaw,
      amountDrawnRaw: 0,
    };
  }

  const rate = annualRate / 100;
  return {
    interestForPeriod: format(amountDrawn * (rate / 365) * daysDrawn),
    monthlyInterest: format((amountDrawn * rate) / 12),
    annualInterest: format(amountDrawn * rate),
    availableHeadroom: format(availableHeadroomRaw),
    availableHeadroomRaw,
    amountDrawnRaw: amountDrawn,
  };
}

// ─── Car Depreciation ─────────────────────────────────────────────────────────

export type CarDepreciationInput = {
  purchasePrice: number;
  gstAmount: number;
  businessUsePercent: number;
  effectiveLifeYears: number;
};

export type DepreciationYearRow = {
  year: number;
  dvOpeningValue: string;
  dvDepreciation: string;
  dvClosingValue: string;
  pcDepreciation: string;
  pcClosingValue: string;
};

export type CarDepreciationResult = {
  costBase: string;
  businessPortion: string;
  dvRate: string;
  schedule: DepreciationYearRow[];
  dvTotalDepreciation: string;
  pcTotalDepreciation: string;
  avgAnnualDvDeduction: string;
  avgAnnualPcDeduction: string;
};

function calculateCarDepreciation({
  purchasePrice,
  gstAmount,
  businessUsePercent,
  effectiveLifeYears,
}: CarDepreciationInput): CarDepreciationResult {
  const costBase = Math.max(purchasePrice - gstAmount, 0);
  const businessPortion = costBase * (businessUsePercent / 100);

  if (purchasePrice <= 0 || effectiveLifeYears <= 0) {
    return {
      costBase: format(costBase),
      businessPortion: '0.00',
      dvRate: '0.00%',
      schedule: [],
      dvTotalDepreciation: '0.00',
      pcTotalDepreciation: '0.00',
      avgAnnualDvDeduction: '0.00',
      avgAnnualPcDeduction: '0.00',
    };
  }

  const dvRate = 2 / effectiveLifeYears;
  const pcDeprPerYear = businessPortion / effectiveLifeYears;
  const schedule: DepreciationYearRow[] = [];
  let dvOpening = businessPortion;
  let dvTotalDepr = 0;

  for (let year = 1; year <= effectiveLifeYears; year++) {
    const dvDepr = dvOpening * dvRate;
    const dvClosing = Math.max(dvOpening - dvDepr, 0);
    dvTotalDepr += dvDepr;

    schedule.push({
      year,
      dvOpeningValue: format(dvOpening),
      dvDepreciation: format(dvDepr),
      dvClosingValue: format(dvClosing),
      pcDepreciation: format(pcDeprPerYear),
      pcClosingValue: format(Math.max(businessPortion - pcDeprPerYear * year, 0)),
    });

    dvOpening = dvClosing;
  }

  return {
    costBase: format(costBase),
    businessPortion: format(businessPortion),
    dvRate: `${(dvRate * 100).toFixed(2)}%`,
    schedule,
    dvTotalDepreciation: format(dvTotalDepr),
    pcTotalDepreciation: format(businessPortion),
    avgAnnualDvDeduction: format(dvTotalDepr / effectiveLifeYears),
    avgAnnualPcDeduction: format(pcDeprPerYear),
  };
}

// ─── Construction Loan ────────────────────────────────────────────────────────

export type ConstructionLoanInput = {
  landCost: number;
  buildCost: number;
  contingencyPercent: number;
  deposit: number;
  annualRate: number;
  constructionMonths: number;
  totalTermYears: number;
  milestonePercents: [number, number, number, number, number];
};

export type MilestoneRow = {
  label: string;
  drawAmount: string;
  cumulativeDrawn: string;
  monthlyInterest: string;
};

export type ConstructionLoanResult = {
  totalProjectCost: string;
  totalLoanRequired: string;
  lvr: string;
  lvrWarning: boolean;
  ioInterest: string;
  piRepayment: string;
  totalPIRepayments: string;
  totalFinancingCost: string;
  milestoneTable: MilestoneRow[];
};

function calculateConstructionLoan({
  landCost,
  buildCost,
  contingencyPercent,
  deposit,
  annualRate,
  constructionMonths,
  totalTermYears,
  milestonePercents,
}: ConstructionLoanInput): ConstructionLoanResult {
  const totalProjectCost = landCost + buildCost * (1 + contingencyPercent / 100);
  const totalLoanRequired = Math.max(totalProjectCost - deposit, 0);
  const lvr = totalProjectCost > 0 ? (totalLoanRequired / totalProjectCost) * 100 : 0;
  const lvrWarning = lvr > 80;

  const milestoneTable: MilestoneRow[] = MILESTONE_LABELS.map((label, i) => {
    const cumulativeDrawn = milestonePercents
      .slice(0, i + 1)
      .reduce((sum, pct) => sum + buildCost * (pct / 100), 0);
    return {
      label,
      drawAmount: format(buildCost * (milestonePercents[i] / 100)),
      cumulativeDrawn: format(cumulativeDrawn),
      monthlyInterest: format((cumulativeDrawn * (annualRate / 100)) / 12),
    };
  });

  if (totalProjectCost <= 0 || totalLoanRequired <= 0 || totalTermYears <= 0) {
    return {
      totalProjectCost: format(totalProjectCost),
      totalLoanRequired: format(totalLoanRequired),
      lvr: lvr.toFixed(2),
      lvrWarning,
      ioInterest: '0.00',
      piRepayment: '0.00',
      totalPIRepayments: '0.00',
      totalFinancingCost: '0.00',
      milestoneTable,
    };
  }

  const ioInterest = (((totalLoanRequired / 2) * (annualRate / 100)) / 12) * constructionMonths;
  const remainingTermMonths = Math.max(totalTermYears * 12 - constructionMonths, 0);
  const piRepayment =
    remainingTermMonths > 0
      ? pmt(annualRate / 100 / 12, remainingTermMonths, -totalLoanRequired)
      : 0;
  const totalPIRepayments = piRepayment * remainingTermMonths;
  const totalFinancingCost = Math.max(ioInterest + totalPIRepayments - totalLoanRequired, 0);

  return {
    totalProjectCost: format(totalProjectCost),
    totalLoanRequired: format(totalLoanRequired),
    lvr: lvr.toFixed(2),
    lvrWarning,
    ioInterest: format(ioInterest),
    piRepayment: format(piRepayment),
    totalPIRepayments: format(totalPIRepayments),
    totalFinancingCost: format(totalFinancingCost),
    milestoneTable,
  };
}

// ─── Early Repayment ──────────────────────────────────────────────────────────

export type EarlyRepaymentInput = {
  outstandingBalance: number;
  annualRate: number;
  remainingTermYears: number;
  repaymentFrequency: RepaymentFrequency;
  lumpSumPayment: number;
  additionalPerPeriod: number;
};

export type EarlyRepaymentResult = {
  standardRepayment: string;
  paymentLabel: string;
  standardTotalInterestValue: number;
  standardTotalInterest: string;
  newRepaymentPerPeriod: string;
  newTermYears: string;
  timeSavedYears: string;
  newTotalInterestValue: number;
  newTotalInterest: string;
  interestSaved: string;
  canCalculate: boolean;
};

function formatTimeSaved(years: number): string {
  const totalMonths = Math.round(years * 12);
  const yrs = Math.floor(totalMonths / 12);
  const mos = totalMonths % 12;
  if (yrs > 0 && mos > 0) return `${yrs} yr${yrs !== 1 ? 's' : ''} ${mos} mo`;
  if (yrs > 0) return `${yrs} yr${yrs !== 1 ? 's' : ''}`;
  return `${mos} mo`;
}

function calculateEarlyRepayment({
  outstandingBalance,
  annualRate,
  remainingTermYears,
  repaymentFrequency,
  lumpSumPayment,
  additionalPerPeriod,
}: EarlyRepaymentInput): EarlyRepaymentResult {
  const freq = PAYMENTS_PER_YEAR[repaymentFrequency];
  const periodicRate = annualRate / 100 / freq;
  const nPeriods = remainingTermYears * freq;
  const paymentLabel = PAYMENT_LABELS[repaymentFrequency];

  if (outstandingBalance <= 0 || remainingTermYears <= 0) {
    return {
      standardRepayment: '0.00',
      paymentLabel,
      standardTotalInterestValue: 0,
      standardTotalInterest: '0.00',
      newRepaymentPerPeriod: '0.00',
      newTermYears: '0.0 years',
      timeSavedYears: '0 mo',
      newTotalInterestValue: 0,
      newTotalInterest: '0.00',
      interestSaved: '0.00',
      canCalculate: false,
    };
  }

  const standardRepayment = pmt(periodicRate, nPeriods, -outstandingBalance);
  const standardTotalInterest = Math.max(standardRepayment * nPeriods - outstandingBalance, 0);
  const balanceAfterLumpSum = Math.max(outstandingBalance - lumpSumPayment, 0);
  const newRepaymentPerPeriod = standardRepayment + additionalPerPeriod;

  if (balanceAfterLumpSum <= 0) {
    return {
      standardRepayment: format(standardRepayment),
      paymentLabel,
      standardTotalInterestValue: standardTotalInterest,
      standardTotalInterest: format(standardTotalInterest),
      newRepaymentPerPeriod: format(newRepaymentPerPeriod),
      newTermYears: '0.0 years',
      timeSavedYears: formatTimeSaved(remainingTermYears),
      newTotalInterestValue: 0,
      newTotalInterest: '0.00',
      interestSaved: format(standardTotalInterest),
      canCalculate: true,
    };
  }

  const minPayment = balanceAfterLumpSum * periodicRate;
  const canCalculate = newRepaymentPerPeriod > minPayment;

  if (!canCalculate) {
    return {
      standardRepayment: format(standardRepayment),
      paymentLabel,
      standardTotalInterestValue: standardTotalInterest,
      standardTotalInterest: format(standardTotalInterest),
      newRepaymentPerPeriod: format(newRepaymentPerPeriod),
      newTermYears: `${remainingTermYears.toFixed(1)} years`,
      timeSavedYears: '0 mo',
      newTotalInterestValue: 0,
      newTotalInterest: '0.00',
      interestSaved: '0.00',
      canCalculate: false,
    };
  }

  const newTermPeriods = nper(periodicRate, -newRepaymentPerPeriod, balanceAfterLumpSum);
  const newTermYearsNum = newTermPeriods / freq;
  const timeSavedInYears = Math.max(remainingTermYears - newTermYearsNum, 0);
  const newTotalInterest = Math.max(
    newRepaymentPerPeriod * newTermPeriods - balanceAfterLumpSum,
    0
  );
  const interestSaved = Math.max(standardTotalInterest - newTotalInterest, 0);

  return {
    standardRepayment: format(standardRepayment),
    paymentLabel,
    standardTotalInterestValue: standardTotalInterest,
    standardTotalInterest: format(standardTotalInterest),
    newRepaymentPerPeriod: format(newRepaymentPerPeriod),
    newTermYears: `${newTermYearsNum.toFixed(1)} years`,
    timeSavedYears: formatTimeSaved(timeSavedInYears),
    newTotalInterestValue: newTotalInterest,
    newTotalInterest: format(newTotalInterest),
    interestSaved: format(interestSaved),
    canCalculate: true,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSpecialtyCalculators() {
  return {
    calculateBorrowingPower,
    calculateBusinessOverdraft,
    calculateCarDepreciation,
    calculateConstructionLoan,
    calculateEarlyRepayment,
  };
}
